import type { PaginatedResponseType } from '~/types/api'
import api, { gql } from './api'

export type TipoPersonaType = 'natural' | 'juridica'

/**
 * Etapa del embudo en la que está el cliente. Es la `key` de una de las etapas
 * configuradas en `companies.settings.customerStates`, así que NO puede ser una
 * unión cerrada: cada empresa define las suyas y las etiquetas y colores salen
 * de esa configuración, no de aquí.
 */
export type EstadoClienteType = string

export interface CustomerContactType {
  id: number
  companyId: string
  customerId: number
  name: string
  position?: string | null
  email?: string | null
  phone?: string | null
  celular?: string | null
  principal: boolean
  notes?: string | null
  createdAt: string
  updatedAt: string
}

/** Ubicación geográfica de una dirección: punto (lat/lng) o polígono de puntos. */
export interface CustomerAddressGeometryType {
  lat?: number | null
  lng?: number | null
  polygon?: Array<[number, number]> | null
}

export interface CustomerAddressType {
  id: number
  companyId: string
  customerId: number
  label?: string | null
  address: string
  barrio?: string | null
  ciudad?: string | null
  departamento?: string | null
  pais?: string | null
  codigoPostal?: string | null
  lat?: number | null
  lng?: number | null
  polygon?: Array<[number, number]> | null
  principal: boolean
  createdAt: string
}

export interface CustomerBankType {
  id: number
  companyId: string
  customerId: number
  banco: string
  tipoCuenta?: string | null
  numeroCuenta?: string | null
  titular?: string | null
  documento?: string | null
  principal: boolean
  notes?: string | null
  createdAt: string
}

export interface CustomerActivityType {
  id: number
  companyId: string
  customerId: number
  type: 'created' | 'quote' | 'invoice' | 'payment' | 'call' | 'email' | 'note' | 'blocked' | 'custom'
  title: string
  description?: string | null
  occurredAt: string
  userId?: number | null
  createdAt: string
}

export interface CustomerNoteType {
  id: number
  companyId: string
  customerId: number
  note: string
  userId?: number | null
  createdAt: string
}

/**
 * Documento adjunto del cliente. El binario vive en el disco del servidor: aquí
 * sólo viajan los metadatos, y el contenido se pide al endpoint de descarga.
 */
export interface CustomerDocumentType {
  id: number
  companyId: string
  customerId: number
  name: string
  storedName: string
  mimeType: string
  size: number
  /**
   * Ruta de descarga, relativa a la base de la API. La calcula el backend en
   * cada respuesta: no está en la tabla, así que no se queda obsoleta si cambia
   * el prefijo. Apunta al endpoint protegido, no al archivo en disco.
   */
  url: string
  categoryId?: number | null
  category?: { id: number; key: string; label: string } | null
  description?: string | null
  userId?: number | null
  createdAt: string
  updatedAt: string
}

/** Tarjeta de cliente en el tablero del CRM: sólo lo que la tarjeta necesita. */
export interface BoardCustomerType {
  id: number
  name: string
  razonSocial?: string | null
  nombreComercial?: string | null
  estadoCliente: string
  phone?: string | null
  celular?: string | null
  email?: string | null
  clasificacion?: string | null
  fechaUltimoContacto?: string | null
  proximoContacto?: string | null
  createdAt: string
  /** Suma de sus cotizaciones vivas (sin rechazadas ni canceladas). */
  amount: number
}

/** Prospecto sin convertir: ocupa la primera columna del tablero. */
export interface BoardLeadType {
  id: number
  name: string
  email?: string | null
  phone?: string | null
  interest?: string | null
  source?: string | null
  createdAt: string
}

export interface CustomerType {
  id: number
  companyId: string
  // Identificación
  tipoPersona: TipoPersonaType
  documentType?: string | null
  documentTypeId?: number | null
  document?: string | null
  digitoVerificacion?: string | null
  name: string
  lastName?: string | null
  razonSocial?: string | null
  nombreComercial?: string | null
  // Contacto
  phone?: string | null
  celular?: string | null
  email?: string | null
  paginaWeb?: string | null
  // Dirección
  address?: string | null
  barrio?: string | null
  ciudad?: string | null
  departamento?: string | null
  pais?: string | null
  codigoPostal?: string | null
  // Tributario
  regimenTributario?: string | null
  responsableIva: boolean
  autoretenedor: boolean
  granContribuyente: boolean
  agenteRetencion: boolean
  codigoCiiu?: string | null
  obligacionesFiscales?: string | null
  // Comercial
  vendedorId?: number | null
  listaPreciosId?: number | null
  /** La columna `numeric` llega como texto desde la API: normalizar con toNumber. */
  cupoCredito?: number | string
  diasCredito?: number
  descuentoPorDefecto?: number | string
  moneda: string
  // Cartera
  saldoActual?: number | string
  saldoVencido?: number | string
  ultimaCompra?: string | null
  ultimaFactura?: string | null
  totalCompras?: number | string
  cantidadFacturas?: number
  // Facturación electrónica
  correoFacturacion?: string | null
  // CRM
  origenCliente?: string | null
  clasificacion?: string | null
  estadoCliente: EstadoClienteType
  calificacion?: number
  fechaUltimoContacto?: string | null
  proximoContacto?: string | null
  asesorId?: number | null
  // Configuración
  aceptaCredito: boolean
  bloqueado: boolean
  motivoBloqueo?: string | null
  // Estado
  status: number
  createdAt: string
  updatedAt: string
  // Relaciones
  contacts?: CustomerContactType[]
  documents?: CustomerDocumentType[]
  addresses?: CustomerAddressType[]
  banks?: CustomerBankType[]
  activities?: CustomerActivityType[]
  notes?: CustomerNoteType[]
  vendedor?: { id: number; name: string } | null
  asesor?: { id: number; name: string } | null
}

export interface CreateCustomerPayloadType {
  tipoPersona?: TipoPersonaType
  documentType?: string
  documentTypeId?: number
  document?: string
  digitoVerificacion?: string
  name: string
  lastName?: string
  razonSocial?: string
  nombreComercial?: string
  phone?: string
  celular?: string
  email?: string
  paginaWeb?: string
  address?: string
  barrio?: string
  ciudad?: string
  departamento?: string
  pais?: string
  codigoPostal?: string
  regimenTributario?: string
  responsableIva?: boolean
  autoretenedor?: boolean
  granContribuyente?: boolean
  agenteRetencion?: boolean
  codigoCiiu?: string
  obligacionesFiscales?: string
  vendedorId?: number
  listaPreciosId?: number
  cupoCredito?: number
  diasCredito?: number
  descuentoPorDefecto?: number
  moneda?: string
  correoFacturacion?: string
  origenCliente?: string
  clasificacion?: string
  estadoCliente?: EstadoClienteType
  calificacion?: number
  fechaUltimoContacto?: string
  proximoContacto?: string
  asesorId?: number
  aceptaCredito?: boolean
  bloqueado?: boolean
  motivoBloqueo?: string
  status?: number
  contacts?: Array<Omit<CustomerContactType, 'id' | 'companyId' | 'customerId' | 'createdAt' | 'updatedAt'>>
  addresses?: Array<Omit<CustomerAddressType, 'id' | 'companyId' | 'customerId' | 'createdAt'>>
  banks?: Array<Omit<CustomerBankType, 'id' | 'companyId' | 'customerId' | 'createdAt'>>
  notes?: Array<{ note: string }>
}

export type UpdateCustomerPayloadType = Partial<CreateCustomerPayloadType>

export interface CustomerListParamsType {
  page?: number
  limit?: number
  name?: string
  document?: string
  tipoPersona?: string
  documentType?: string
  phone?: string
  email?: string
  ciudad?: string
  departamento?: string
  estadoCliente?: string
  clasificacion?: string
  status?: number
}

export interface CustomerStatsType {
  total: number
  activos: number
  inactivos: number
  conDeuda: number
  morosos: number
  nuevosMes: number
}

/** Los estados del cliente ya no viven aquí: vienen de `companies.settings.customerStates`. */

export const customerService = {
  async list(params?: CustomerListParamsType) {
    const { data } = await api.get('/customers', { params })
    return data.data as PaginatedResponseType<CustomerType>
  },

  async stats() {
    const { data } = await api.get('/customers/stats')
    return data.data as CustomerStatsType
  },

  async get(id: number) {
    const { data } = await api.get(`/customers/${id}`)
    return data.data as CustomerType
  },

  async create(payload: CreateCustomerPayloadType) {
    const { data } = await api.post('/customers', payload)
    return data.data as CustomerType
  },

  async update(id: number, payload: UpdateCustomerPayloadType) {
    const { data } = await api.put(`/customers/${id}`, payload)
    return data.data as CustomerType
  },

  async delete(id: number) {
    const { data } = await api.delete(`/customers/${id}`)
    return data.data as boolean
  },

  /** Elimina varios clientes de una vez. Devuelve cuántos se borraron. */
  async bulkDelete(ids: number[]) {
    const { data } = await api.post('/customers/bulk-delete', { ids })
    return data.data as { deleted: number }
  },

  // ── Sub-recursos ──────────────────────────────────────────────────────────
  async addContact(customerId: number, payload: Omit<CustomerContactType, 'id' | 'companyId' | 'customerId' | 'createdAt' | 'updatedAt'>) {
    const { data } = await api.post(`/customers/${customerId}/contacts`, payload)
    return data.data as CustomerContactType
  },

  async updateContact(customerId: number, contactId: number, payload: Record<string, unknown>) {
    const { data } = await api.put(`/customers/${customerId}/contacts/${contactId}`, payload)
    return data.data as CustomerContactType
  },

  async deleteContact(customerId: number, contactId: number) {
    const { data } = await api.delete(`/customers/${customerId}/contacts/${contactId}`)
    return data.data as boolean
  },

  async addAddress(customerId: number, payload: Omit<CustomerAddressType, 'id' | 'companyId' | 'customerId' | 'createdAt'>) {
    const { data } = await api.post(`/customers/${customerId}/addresses`, payload)
    return data.data as CustomerAddressType
  },

  async deleteAddress(customerId: number, addressId: number) {
    const { data } = await api.delete(`/customers/${customerId}/addresses/${addressId}`)
    return data.data as boolean
  },

  async updateAddress(customerId: number, addressId: number, payload: Omit<CustomerAddressType, 'id' | 'companyId' | 'customerId' | 'createdAt'>) {
    const { data } = await api.put(`/customers/${customerId}/addresses/${addressId}`, payload)
    return data.data as CustomerAddressType
  },

  async addBank(customerId: number, payload: Omit<CustomerBankType, 'id' | 'companyId' | 'customerId' | 'createdAt'>) {
    const { data } = await api.post(`/customers/${customerId}/banks`, payload)
    return data.data as CustomerBankType
  },

  async updateBank(customerId: number, bankId: number, payload: Partial<Omit<CustomerBankType, 'id' | 'companyId' | 'customerId' | 'createdAt'>>) {
    const { data } = await api.put(`/customers/${customerId}/banks/${bankId}`, payload)
    return data.data as CustomerBankType
  },

  async deleteBank(customerId: number, bankId: number) {
    const { data } = await api.delete(`/customers/${customerId}/banks/${bankId}`)
    return data.data as boolean
  },

  async addActivity(customerId: number, payload: { type?: string; title: string; description?: string; occurredAt?: string }) {
    const { data } = await api.post(`/customers/${customerId}/activities`, payload)
    return data.data as CustomerActivityType
  },

  async addNote(customerId: number, note: string) {
    const { data } = await api.post(`/customers/${customerId}/notes`, { note })
    return data.data as CustomerNoteType
  },

  /**
   * Todo lo que pinta el tablero del CRM, en una sola llamada: los clientes con
   * su importe cotizado vigente y los prospectos que aún no se han convertido.
   */
  async board() {
    const { data } = await api.get('/customers/board')
    return data.data as { customers: BoardCustomerType[]; leads: BoardLeadType[] }
  },

  /**
   * El mismo tablero, por GraphQL: pide sólo los campos que pinta la tarjeta en
   * vez de las más de cuarenta columnas de la tabla.
   *
   * Convive con `board()` en lugar de sustituirlo, a propósito. GraphQL se añade
   * sin tocar nada de lo que ya funciona: el REST sigue siendo el camino por
   * defecto del panel y lo que consumen las aplicaciones de fuera. Para pasar el
   * tablero a GraphQL basta con cambiar la llamada en la pantalla del CRM, y
   * volver atrás es cambiarla de vuelta.
   */
  async boardGql() {
    const { crmBoard } = await gql<{
      crmBoard: { customers: BoardCustomerType[]; leads: BoardLeadType[] }
    }>(`
      {
        crmBoard {
          customers {
            id name razonSocial nombreComercial estadoCliente
            phone celular email clasificacion
            fechaUltimoContacto proximoContacto createdAt amount
          }
          leads { id name email phone interest source createdAt }
        }
      }
    `)
    return crmBoard
  },

  // ── Documentos ────────────────────────────────────────────────────────────
  async listDocuments(customerId: number) {
    const { data } = await api.get(`/customers/${customerId}/documents`)
    return data.data as CustomerDocumentType[]
  },

  /** Sube el archivo como multipart: es el único endpoint que no habla JSON. */
  async uploadDocument(customerId: number, file: File, meta: { categoryId?: number | null; description?: string }) {
    const form = new FormData()
    form.append('file', file)
    if (meta.categoryId) form.append('categoryId', String(meta.categoryId))
    if (meta.description) form.append('description', meta.description)
    // Sin Content-Type explícito: el navegador debe poner el boundary del multipart.
    const { data } = await api.post(`/customers/${customerId}/documents`, form)
    return data.data as CustomerDocumentType
  },

  /**
   * El endpoint exige Authorization y X-Company-ID, así que un `<a href>` directo
   * daría 401: hay que traerlo con el cliente ya autenticado y envolverlo en un blob.
   * Quien llame es responsable de liberar la URL con `URL.revokeObjectURL`.
   */
  async fetchDocumentBlobUrl(doc: CustomerDocumentType) {
    const { data } = await api.get(doc.url, { responseType: 'blob' })
    return URL.createObjectURL(data as Blob)
  },

  /** Sólo metadatos: para cambiar el archivo hay que subir otro documento. */
  async updateDocument(customerId: number, documentId: number, payload: { categoryId?: number | null; description?: string | null }) {
    const { data } = await api.put(`/customers/${customerId}/documents/${documentId}`, payload)
    return data.data as CustomerDocumentType
  },

  async deleteDocument(customerId: number, documentId: number) {
    const { data } = await api.delete(`/customers/${customerId}/documents/${documentId}`)
    return data.data as boolean
  }
}
